import { useState, useCallback, useTransition, useEffect, useMemo } from "react";
import { defineMessages } from "react-intl";

import { useFormatMessage } from "../Intl/Context";
import { useAuthenticationManager } from "../GeneralServices/AuthenticationService";
import BottomSheetPanel from "../Panels/BottomSheetPanel";
import SuggestionCard from "./SuggestionCard";
import FunnelCompletion from "./FunnelCompletion";

const ContributionsReviewFunnel = ({
  contributionIds,
  restartKey,
  onResume,
  onClose,
  onComplete,
  onSeeMore,
  isSeekingMore,
  hasNoMoreSuggestions
}) => {
  const t = useFormatMessage();
  const { isAuthenticationShown } = useAuthenticationManager();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Wrap index changes in a transition so React keeps the current card
  // visible while the next one's Relay query resolves, instead of falling
  // back to the Suspense spinner.
  const [isPending, startTransition] = useTransition();

  const total = contributionIds.length;

  // When the parent signals a new batch of suggestions is ready, restart from the
  // first card. Wrapped in a transition to keep the completion screen visible while
  // the first card's query resolves.
  useEffect(() => {
    startTransition(() => {
      setCurrentIndex(0);
      setIsComplete(false);
    });
  }, [restartKey]);

  // When clicking on the "next" button, either move to the next card or show the
  // completion screen if on the last card.
  const handleNext = useCallback(() => {
    if (currentIndex >= total - 1) {
      setIsComplete(true);
    } else {
      startTransition(() => {
        setCurrentIndex(i => i + 1);
      });
    }
  }, [currentIndex, total]);

  // When clicking on the "previous" button, move to the previous card if not on the first
  // one (which shouldn't happen as the button is not rendered).
  const handlePrevious = useCallback(() => {
    if (currentIndex === 0) return;

    startTransition(() => {
      setCurrentIndex(i => Math.max(0, i - 1));
    });
  }, [currentIndex]);

  const title = useMemo(
    () =>
      t(intlMessages.title, {
        current: <b key="current">{currentIndex + 1}</b>,
        total: <b key="total">{total}</b>
      }),
    [currentIndex, total, t]
  );

  return (
    <BottomSheetPanel
      open={true}
      title={title}
      onDismiss={onClose}
      trapFocus={!isAuthenticationShown}
      className="contributions-review-funnel-panel"
    >
      {!isComplete && (
        <SuggestionCard
          contributionId={contributionIds[currentIndex]}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isFirst={currentIndex === 0}
          isLast={currentIndex === total - 1}
          isPending={isPending}
          onComplete={onComplete}
        />
      )}

      {isComplete && (
        <FunnelCompletion
          onResume={onResume}
          onSeeMore={onSeeMore}
          isSeekingMore={isSeekingMore}
          hasNoMoreSuggestions={hasNoMoreSuggestions}
        />
      )}
    </BottomSheetPanel>
  );
};

export default ContributionsReviewFunnel;

const intlMessages = defineMessages({
  title: {
    id: "contributionsReviewFunnel.title",
    defaultMessage: "Contribution similaire {current} sur {total}"
  }
});
